import { getAppLocale, getAppLocaleAsObject } from '../app-locale';
import { api_base_url, AUTHENTIFICATION_IMPLEMENTATION, front_base_url, seamlessEnvironments } from '../../constants';
import { appConfiguration, setSeamlessSession, setSeamlessSessionWithOkta } from '../../utils/app-starter';
import { defaultLocaleData } from '../../locales';
import { buildMySchneiderRedirectionUrl } from '../../utils/url-builder';

const https = require('https');
const axios = require('axios');

const SE_DATA_POSTFIX = 'products/msx/';

export const API_INSTANCE = axios.create({
  withCredentials: true
});

export const API_INSTANCE_NO_CREDENTIALS = axios.create({
  withCredentials: false
});

export const EXTERNAL_API_INSTANCE = axios.create({
  httpsAgent: new https.Agent({
    rejectUnauthorized: false
  }),
  headers: {
    'Content-Type': 'application/json;charset=UTF-8',
  },
  withCredentials: true
});

export function parseResponseData(response) {
  return response.then(res => res.data);
}
export function setDefaultApiLocale(locale) {
  axios.defaults.params = {
    locale
  };
};
export function getDefaultApiLocale() {
  return axios.defaults.params.locale;
};
export function setDefaultApiPath(url = '') {
  API_INSTANCE.defaults.baseURL = `${url}/${SE_DATA_POSTFIX}`;
}

export function fetchProductInformations(productRefs) {
  const defaultLocale = 'en-GB';
  const locale = getAppLocale() || defaultLocale;
  const responseLocale = locale === 'en-WW' ? defaultLocale : locale;
  return parseResponseData(
    axios.create({
      httpsAgent: new https.Agent({
        rejectUnauthorized: false,
      }),
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
        "Authorization": "Bearer afNuEJRXrfP8QqIlz_z1m0mWvKfAGK7W",
      },
    })
      .post(`https://gw-api.schneider-electric.com/sc/products/description/locale/${locale}`, {
        'refIds': productRefs,
      }, {
        withCredentials: true,
      })
  )
    .then(payload => {
      const catalogProducts = payload || [];
      return catalogProducts.map(catalogProduct => catalogProduct?.catalogue_product)
        .map(product => {
          const id = product?.product_id || '';
          const translation = product?.long_desc?.translation || {};

          let description = '';
          if (translation.hasOwnProperty(responseLocale)) {
            description = translation[responseLocale];
          } else if (translation.hasOwnProperty(defaultLocale)) {
            description = translation[defaultLocale];
          }

          return {
            'id': id,
            'description': description,
            'ean': product.ean,
            'enum': product.e_num
          };
        });
    });
}

/**
 * Determine which implementation have been used to generate the token.
 * This is a temporary implementation, this information must come from MySchneider (via the idpApp argument)
 *
 * @param {*} token CIAM or OKTA token
 * @returns the implementation used to generate the token
 */
function _getTokenImplementation(token) {
  if (token && !token.startsWith('00D')) {
    return AUTHENTIFICATION_IMPLEMENTATION.OKTA;
  }
  return AUTHENTIFICATION_IMPLEMENTATION.CIAM;
}

function _requestSeamlessSession(token, environment) {
  const tokenUrl = seamlessEnvironments[environment]?.tokenValidationUrl || seamlessEnvironments.prod.tokenValidationUrl;
  return axios.create({
    httpsAgent: new https.Agent({
      rejectUnauthorized: false,
    }),
    headers: {
      "Authorization": `Bearer ${token}`,
    },
  })
.get(tokenUrl);
}

function _requestSeamlessSessionWithOkta(token, environment) {
  return axios.create({
    httpsAgent: new https.Agent({
      rejectUnauthorized: false,
    }),
    headers: {
      "Authorization": `Bearer ${token}`,
    },
  })
  .post(`${api_base_url}/okta-user-information`, {
    environment,
    oktaToken: token
  });
}

export function validateSeamlessSession() {
  const applicationUrl = buildMySchneiderRedirectionUrl();
  return API_INSTANCE.get(applicationUrl).then(() => {
    document.location.href = applicationUrl;
    return true;
  }).catch(() => {
    // Seamless session in invalid / error
    console.log('Session is no longer valid');
    return false;
  });
}

export function fetchSeamless(token, environment, authentificationImplementation) {
  if (authentificationImplementation === AUTHENTIFICATION_IMPLEMENTATION.OKTA
      || _getTokenImplementation(token) === AUTHENTIFICATION_IMPLEMENTATION.OKTA) {
    return _requestSeamlessSessionWithOkta(token, environment)
    .then(result => {
      setSeamlessSessionWithOkta(result.data.payload);
    }).catch(error => {
      console.log("Can't connect to Seamless session");
      console.log(error);
    });
  }

  return _requestSeamlessSession(token, environment)
    .then(payload => {
      setSeamlessSession(payload.data);
    }).catch(error => {
      console.log("Can't connect to Seamless session");
      console.log(error);
    });
}

export function fetchMySchneiderCountries() {
  return API_INSTANCE_NO_CREDENTIALS.get(`${front_base_url}/countries.json`).then((payload) => { return Object.values(payload?.data); }).catch((error) => { return []; })
}


export const fetchTranslationFileData = async () => {
  const { lang, country } = getAppLocaleAsObject();
  const fileSuffix = `${lang}_${country}`.toLocaleLowerCase();

  let labels = defaultLocaleData;
  let countryFileData;
  let overrideFileData;
  let defaultTranslationFileData;

  await Promise.allSettled([API_INSTANCE_NO_CREDENTIALS.get(`${front_base_url}/translations/locale_${fileSuffix}.json`).then((payload) => { countryFileData = payload.data; }).catch((error) => { }),
  API_INSTANCE_NO_CREDENTIALS.get(`${front_base_url}/translations/default-translation.json`).then((payload) => { defaultTranslationFileData = payload.data; }).catch((error) => { }),
  API_INSTANCE_NO_CREDENTIALS.get(`${front_base_url}/translations/locale_${fileSuffix}_override.json`).then((payload) => { overrideFileData = payload.data; }).catch((error) => { })
  ]
  );

  if (defaultTranslationFileData) {
    Object.assign(labels, defaultTranslationFileData);
  }
  if (countryFileData) {
    Object.assign(labels, countryFileData);
  }

  if (overrideFileData) {
    Object.assign(labels, overrideFileData);
  }

  return labels;
}


export const checkIsPublicInternal = async () => {

  return API_INSTANCE_NO_CREDENTIALS.get(`https://www-pre.se.com/ww/confsdl/mcc-front/customer_static/distributor-toolkit/File_For_testing_VPN_Access_DO_NOT_DELETE.html`);
}

export const checkEdgePartnerAvailability = async () => {
    return API_INSTANCE_NO_CREDENTIALS.post(`${api_base_url}/check-edge-distributors`, {
      'code': btoa(appConfiguration.domain)
    });
}
